"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";

const STORAGE_KEY = "cultap:favoritos";

function readLocal(): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(ids: number[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

/**
 * Favorites work with or without an account: anonymous visitors get
 * localStorage, and logging in switches to server-side favorites (synced
 * once from whatever was saved locally, so nothing is lost).
 */
export function useFavorites() {
  const { status } = useSession();
  const [favorites, setFavorites] = useState<number[]>([]);
  const syncedRef = useRef(false);

  useEffect(() => {
    if (status === "loading") return;

    if (status === "unauthenticated") {
      setFavorites(readLocal());
      return;
    }

    if (status === "authenticated" && !syncedRef.current) {
      syncedRef.current = true;
      const local = readLocal();
      const request = local.length
        ? fetch("/api/favorites/sync", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ eventIds: local }),
          })
        : fetch("/api/favorites");

      request
        .then((res) => res.json())
        .then((data) => {
          setFavorites(data.eventIds ?? []);
          if (local.length) window.localStorage.removeItem(STORAGE_KEY);
        })
        .catch(() => {});
    }
  }, [status]);

  const toggle = useCallback(
    (eventId: number) => {
      setFavorites((current) => {
        const isFavorite = current.includes(eventId);
        const next = isFavorite ? current.filter((id) => id !== eventId) : [...current, eventId];

        if (status === "authenticated") {
          if (isFavorite) {
            fetch(`/api/favorites/${eventId}`, { method: "DELETE" }).catch(() => {});
          } else {
            fetch("/api/favorites", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ eventId }),
            }).catch(() => {});
          }
        } else {
          writeLocal(next);
        }
        return next;
      });
    },
    [status]
  );

  const isFavorite = useCallback((eventId: number) => favorites.includes(eventId), [favorites]);

  return { favorites, toggle, isFavorite };
}
